import { ConversionWarning } from '@nx-ahs/formly/shared';
import { TripettoNode, TripettoI18Model } from './tripetto-interfaces';

/**
 * Generate a conversion warning
 */
export function generateWarning(
	severity: 'error' | 'warning' | 'info',
	nodeId: string,
	nodeName: string,
	blockType: string,
	message: string,
	suggestion?: string,
	context?: any
): ConversionWarning {
	return {
		severity,
		nodeId,
		nodeName,
		blockType,
		message,
		suggestion,
		context
	};
}

/**
 * Generate warning for languages block (i18n limitation)
 */
export function generateLanguagesBlockWarning(node: TripettoNode): ConversionWarning {
	const i18Models: TripettoI18Model[] = node?.i18Models || [];
	const availableLanguages = [...new Set(i18Models.map((m) => m.langCode))];
	const defaultLang = availableLanguages.includes('en') ? 'en' : availableLanguages[0] || 'en';

	return generateWarning(
		'warning',
		node.id,
		node.name || 'Unnamed Node',
		node.block?.type || 'languages',
		`Languages block converted to static text (language: ${defaultLang}). Dynamic language switching is not supported in formly.`,
		`This block had translations for: ${availableLanguages.join(', ')}. A mat-formly-i18n component is needed for full i18n support.`
	);
}

/**
 * Generate warning for setter block (requires manual configuration)
 */
export function generateSetterBlockWarning(
	node: TripettoNode,
	slotLookup: Map<string, { alias?: string; nodeName?: string; slotId?: string }>
): ConversionWarning | null {
	const values = node.block?.values || [];

	if (values.length === 0) {
		return null;
	}

	const mappings: string[] = [];

	values.forEach((value: any) => {
		// Find the target field information using cluster-wide lookup
		const targetVariable = value.variable;
		const targetSlot = slotLookup.get(targetVariable);
		const fieldIdentifier = targetSlot?.alias || targetVariable || 'unknown';

		// Build human-readable mapping description
		let mapping = '';

		if (value.load) {
			// Loading from linked entity
			const entityField = value.value || 'unknown';
			const whereClause = value.loadWhereColumn
				? ` WHERE ${value.loadWhereColumn} ${value.loadWhereClause || '=='} ${value.loadWhereValue}`
				: value.loadIndex
					? ' [using mutable form index]'
					: ' [first row]';
			mapping = `Field "${fieldIdentifier}" ← entity["${entityField}"]${whereClause}`;
		} else if (value.querystring) {
			// Loading from query string
			mapping = `Field "${fieldIdentifier}" ← query param "${value.value}"`;
		} else if (typeof value.value === 'string' && value.value.startsWith('@')) {
			// Loading from another variable
			mapping = `Field "${fieldIdentifier}" ← ${value.value}`;
		} else {
			// Static value
			mapping = `Field "${fieldIdentifier}" = ${JSON.stringify(value.value)}`;
		}

		// Add mode information if present
		if (value.mode) {
			mapping += ` [mode: ${value.mode}]`;
		}
		if (value.lock !== undefined) {
			mapping += ` [lock: ${value.lock}]`;
		}

		mappings.push(mapping);
	});

	return generateWarning(
		'warning',
		node.id,
		node.name || 'Setter Block',
		node.block?.type || 'setter',
		`Setter block converted to hidden field. Linked entity preloading must be configured manually in Formly.`,
		`This setter had ${values.length} value mapping(s):\n${mappings.map((m) => `  • ${m}`).join('\n')}\n\nIn Formly, handle data preloading by:\n1. Fetching linked entity data before form initialization\n2. Setting initial model values for the fields listed above\n3. Use field expressions or hooks if transformation logic is needed (modes, calculations, etc.)`
	);
}
